import React, { useEffect } from 'react';
import { View } from 'react-native';
import moment from 'moment';

import Text from '../../components/text/Text';
import { useUserContext } from '../../../common/utils/context';
import { useConfirmStockLevelsContext } from '../../utils/context';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { SecondaryButton } from '../../design/components';

// A market line can reach zero through sales with old stock still on the shelf, so its
// date stays until a driver says otherwise here. A line the driver counts down to zero
// clears its date without the press, so it only says what will happen.
const NoStockOnShelfAction = ({ planogramItem }) => {
	const styles = useThemedStyles(createStyles);
	const { tenantSettings } = useUserContext();
	const { changeStockItem, isMarket } = useConfirmStockLevelsContext();
	const { expiryDates, initialPresentProductQuantity, noStockOnShelf, presentProductQuantity } = planogramItem;
	const keptDate = expiryDates?.[1];

	// Finding stock cancels the press, so going back to zero needs a new one.
	useEffect(() => {
		if (noStockOnShelf && presentProductQuantity) {
			changeStockItem(planogramItem.id, 'noStockOnShelf')(false);
		}
	}, [changeStockItem, noStockOnShelf, planogramItem.id, presentProductQuantity]);

	if (!isMarket || !tenantSettings.useExpiryDates || !keptDate) {
		return null;
	}

	const date = moment(keptDate).format('MMM DD');

	if (initialPresentProductQuantity > 0 && presentProductQuantity === 0) {
		return (
			<View style={[styles.container, styles.pressedContainer]}>
				<Text style={[styles.text, styles.pressedText]}>{`The ${date} date will clear when you confirm`}</Text>
			</View>
		);
	}

	if (initialPresentProductQuantity !== 0 || presentProductQuantity) {
		return null;
	}

	return (
		<View style={[styles.container, noStockOnShelf && styles.pressedContainer]}>
			<Text style={[styles.text, noStockOnShelf && styles.pressedText]}>
				{noStockOnShelf ? `The ${date} date will clear when you confirm` : `Shows 0 but still has a ${date} date`}
			</Text>
			<SecondaryButton
				label={noStockOnShelf ? 'Undo' : 'No stock on shelf'}
				onPress={() => changeStockItem(planogramItem.id, 'noStockOnShelf')(!noStockOnShelf)}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	container: {
		gap: space.sm,
		marginTop: space.sm,
	},
	pressedContainer: {
		backgroundColor: colors.successBg,
		borderColor: colors.successCardBorder,
		borderRadius: radius.control,
		borderWidth: 1,
		padding: space.md,
	},
	text: {
		...type.caption,
		color: colors.textSecondary,
	},
	pressedText: {
		...type.bodySmallStrong,
		color: colors.onSuccessBg,
	},
});

export default React.memo(NoStockOnShelfAction);
